import type { Blueprint } from '#layers/studio/app/runtime/builder/base/blueprints/Blueprint';
import type { ValidatableBlueprint } from '#layers/studio/app/runtime/builder/base/blueprints/ValidatableBlueprint';
import type { ValidationErrors } from '#layers/studio/app/runtime/builder/base/types/ValidationErrors';
import type { FormBuilderContract } from '#layers/studio/app/runtime/builder/form';
import type { BlueprintDefinition } from '#layers/studio/app/runtime/builder/form/blueprints/BlueprintDefinition';
import type { VisibleBlueprint } from '#layers/studio/app/runtime/builder/form/blueprints/VisibleBlueprint';
import { AlwaysChoice } from '#layers/studio/app/runtime/builder/form/enums/AlwaysChoice';
import type { InternallyChoice } from '#layers/studio/app/runtime/builder/form/enums/InternallyChoice';
import type { NeverChoice } from '#layers/studio/app/runtime/builder/form/enums/NeverChoice';
import type { WhenChoice } from '#layers/studio/app/runtime/builder/form/enums/WhenChoice';
import type { Entry } from '#layers/studio/app/runtime/builder/form/entries/Entry';
import { entry } from '#layers/studio/app/runtime/builder/form/entries/Entry';
import { ValidEntry } from '#layers/studio/app/runtime/builder/form/entries/ValidEntry';
import type { EntryFactory } from '#layers/studio/app/runtime/builder/form/traits/EntryFactory';
import type { HasWidth } from '#layers/studio/app/runtime/builder/base/traits/HasWidth';
import type { HasHelp } from '#layers/studio/app/runtime/builder/form/traits/HasHelp';
import type { HasLabel } from '#layers/studio/app/runtime/builder/form/traits/HasLabel';
import type { ProcessCallback } from '#layers/studio/app/runtime/builder/form/types/ProcessCallback';
import RecaptchaService from '#layers/core/app/runtime/services/RecaptchaService';

export const Definition: BlueprintDefinition = {
    type: 'recaptcha',
    name: '[[[Recaptcha]]]',
    icon: 'fa-refresh',
    group: 'cms',
    position: 13,
    disabled: true
};

export class RecaptchaEntry extends ValidEntry<string>
{
    public type: string = Definition.type;
    public data: string = null;

    public constructor(data: any = null)
    {
        super();

        if (data !== null)
        {
            this.data = data;
        }
    }

    public async collect(blueprint: RecaptchaContract, form: FormBuilderContract, preprocess: ProcessCallback): Promise<Entry>
    {
        const { $recaptcha } = useRecaptcha();

        await preprocess(blueprint, this, form.blueprintId, form.entryId);

        if (!this.data)
        {
            const recaptchaOptions = await RecaptchaService.fetch();

            await $recaptcha.install(recaptchaOptions.publicKey, recaptchaOptions.isActive);

            if ($recaptcha.active)
            {
                await $recaptcha.execute('login',
                    (captchaResult: string): void =>
                    {
                        this.data = captchaResult;
                    },
                    (ex: any) =>
                    {
                        // on reject captcha
                        this.data = '';
                    });
            }
            else
            {
                this.data = '';
            }
        }

        return entry({
            type: this.type,
            data: this.data
        });
    }

    public validate(blueprint: RecaptchaContract, form: FormBuilderContract): boolean
    {
        this.errors = {};

        return this.valid();
    }
}

export const instanceOfRecaptchaEntry = (object: any): object is RecaptchaEntry =>
{
    return object && 'type' in object && object.type === Definition.type;
};

export interface RecaptchaContract extends Blueprint, VisibleBlueprint, HasWidth, HasLabel, HasHelp
{
}

export class RecaptchaType implements RecaptchaContract, ValidatableBlueprint, EntryFactory<RecaptchaEntry>, HasHelp
{
    public id: string;
    public type: string;
    public name: string;
    public label: Record<string, string>;
    public showLabel: boolean;
    public visible: AlwaysChoice | NeverChoice | InternallyChoice | WhenChoice;
    public visibleWhen: string;
    public width: number;
    public minWidth: number;
    public errors: ValidationErrors;
    public help: Record<string, string>;

    public constructor(id: string, name: string, width: number)
    {
        this.id = id;
        this.type = Definition.type;
        this.name = name;
        this.label = { 'pl-PL': 'Recaptcha' };
        this.showLabel = true;
        this.visible = AlwaysChoice.Always;
        this.visibleWhen = null;
        this.width = Math.min(3, Math.max(this.minWidth, width));
        this.minWidth = 1;
        this.errors = {};
        this.help = {};
    }

    public setDefaultWidth(width: number): void
    {
        this.width = Math.min(3, Math.max(this.minWidth, width));
    }

    public createEntry(data: any): RecaptchaEntry
    {
        return new RecaptchaEntry(data);
    }

    public validate(): Record<string, ValidationErrors>
    {
        this.errors = {};

        return {
            [this.name]: this.errors
        };
    }
}
